import "../../../css/GlobalStyles.css";
import { useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getAccountState } from "../../../redux/slices/accountSlice";
import CreatorModePanelWrapper from "../../../components/CreatorDashboard/CreatorModePanelWrapper";
import { Colors } from "../../../utils/colors";
import ShareProfileLinkModal from "../../../components/CreatorDashboard/CreatorProfile/ShareProfileLinkModal";
import RectangleButton from "../../../components/Buttons/RectangleButton";
import StandardBorderedContainer from "../../../components/Containers/StandardBorderedContainer";
import { MARKITAI } from "@markit/common.utils";
import { Icon } from "@iconify/react";
import ProfilePageFull from "../../../components/ProfilePage/ProfilePageFull";
import { useProfilePageFullData } from "../../../hooks/useProfilePageFullData";
import useAsyncOnMount from "../../../hooks/useAsyncEffectOnMount";
import { NotificationType } from "@markit/common.types";
import { showNotificationBanner } from "../../../utils/notificationUtils";
import { API } from "../../../API";
import { useWindowResize } from "../../../hooks/useWindowResize";
import { isDesktop } from "react-device-detect";
import { HorizontalDivider } from "../../../components/Dividers/HorizontalDivider";

const ProfilePanel = () => {
  const { accountData } = useSelector(getAccountState).account;
  const [shareModal, setShareModal] = useState(false);
  const dispatch = useDispatch();

  const styles = {
    bodyContainer: {
      backgroundColor: Colors.WHITE,
      height: "100%",
    },
  };

  const windowWidth = useWindowResize();
  const isDesktopSize = useMemo(
    () => isDesktop && windowWidth >= 1200,
    [windowWidth]
  );

  const {
    loadingEvents,
    userAttendingEvents,
    userEvents,
    eventOrganizers,
    tabItem,
    setTabItem,
    profilePages,
    setProfilePages,
  } = useProfilePageFullData({ userData: accountData });

  useAsyncOnMount(async () => {
    const { profilePages } = await API.profile.createInitialProfilePages({
      uid: accountData.uid,
    });
    if (profilePages.length > 0) {
      setProfilePages(profilePages);
    }
  });

  const profileUrl = useMemo(
    () => MARKITAI + `/u/${accountData.username}`,
    [accountData.username]
  );

  const copyOnPress = useCallback(() => {
    navigator.clipboard
      .writeText(profileUrl)
      .then(() => {
        showNotificationBanner(
          dispatch,
          "Copied!",
          NotificationType.AFFIRMATIVE
        );
      })
      .catch((err) => {
        alert(err);
      });
  }, [dispatch, profileUrl]);

  const sharedPropsPack = {
    loadingEvents: loadingEvents,
    userAttendingEvents: userAttendingEvents,
    userEvents: userEvents,
    eventOrganizers: eventOrganizers,
    tabItem: tabItem,
    setTabItem: setTabItem,
    profilePages: profilePages,
    setProfilePages: setProfilePages,
  };

  return (
    <div
      className={isDesktopSize ? "RowNormal" : "ColumnNormal"}
      style={isDesktopSize ? { height: "100%", overflow: "hidden" } : {}}
    >
      <div style={{ flex: 1, overflow: "auto" }}>
        <CreatorModePanelWrapper
          title={
            <div className="ColumnNormal" style={{ gap: 7 }}>
              <span style={{ fontSize: 32, fontWeight: 600 }}>Link In Bio</span>
              <span className="bodySubtext">
                Markit makes you a custom Link-in-Bio website for you to
                showcase your links, products, posts,
                <br />
                and more - all in one place. Share your Link in Bio to grow
                textable subscribers and your brand.
              </span>
            </div>
          }
          headerRight={<></>}
        >
          <div style={styles.bodyContainer}>
            <ProfilePageFull {...sharedPropsPack} userData={accountData} />
          </div>
        </CreatorModePanelWrapper>
      </div>
      {!isDesktopSize ? (
        <HorizontalDivider
          altMargin={0}
          altColor={Colors.GRAY11}
          altWidth={1}
        />
      ) : null}
      <div
        className={"ColumnNormal"}
        style={{
          width: 420,
          marginInline: isDesktopSize ? 0 : "auto",
          borderLeft: isDesktopSize ? `1px solid ${Colors.GRAY11}` : "none",
          justifyContent: "center",
        }}
      >
        <div
          className="ColumnNormal"
          style={{
            padding: "30px 60px",
            gap: 30,
            width: 300,
            overflow: "auto",
          }}
        >
          <div className="AlignedRow" style={{ gap: 7 }}>
            <StandardBorderedContainer
              containerStyles={{
                padding: "14px 12px",
                minWidth: 160,
                backgroundColor: Colors.WHITE,
                border: `0.5px solid ${Colors.BLACK}`,
              }}
            >
              <div className="AlignedRowSpaced" style={{ gap: 7 }}>
                <div className="AlignedRow" style={{ gap: 4, minWidth: 0 }}>
                  <Icon
                    icon="mdi:link-variant"
                    height={16}
                    style={{ color: Colors.BLACK }}
                  />
                  <span
                    className="bodySmallMedium TextOverflowEllipsis"
                    style={{ flex: 1, minWidth: 0 }}
                  >
                    {profileUrl}
                  </span>
                </div>
                <Icon
                  icon="ion:copy-outline"
                  height={16}
                  style={{
                    color: Colors.BLACK,
                    flexShrink: 0,
                    cursor: "pointer",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.6")}
                  onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
                  onClick={copyOnPress}
                />
              </div>
            </StandardBorderedContainer>
            <RectangleButton
              buttonLabel="Share Link"
              onPress={() => setShareModal(true)}
              altColor={Colors.BLACK}
              altTextColor={Colors.WHITE}
              containerStyles={{ padding: "14px 12px", whiteSpace: "nowrap" }}
            />
          </div>
          <div
            style={{
              border: `3px solid ${Colors.BLACK}`,
              borderRadius: 36,
              height: 650,
              overflow: "auto",
            }}
          >
            <ProfilePageFull
              {...sharedPropsPack}
              userData={accountData}
              forceMobile
            />
          </div>
        </div>
      </div>
      {shareModal ? (
        <ShareProfileLinkModal
          username={accountData.username}
          setShareLinkModal={setShareModal}
        />
      ) : null}
    </div>
  );
};

export default ProfilePanel;
